import { FORMATS } from './presets';
import { loadImage } from './assets';
import { loadTemplateFonts } from './fonts';

/*
 * Desenha um modelo no canvas. O preview e o download usam o mesmo código,
 * então o arquivo baixado é exatamente o que aparece na tela.
 */

export function resolveText(content, data) {
  return (content || '')
    .replace(/\{titulo\}/g, data.titulo || '')
    .trim();
}

function elementImageUrl(element, data) {
  if (element.type === 'photo') {
    return data.photo;
  }

  return null;
}

/* Carrega fontes e imagens do modelo. Devolve um mapa url → imagem. */
export async function prepareAssets(config, data) {
  const urls = new Set();

  for (const element of config.elements || []) {
    const url = elementImageUrl(element, data);

    if (url && !element.hidden) {
      urls.add(url);
    }
  }

  const images = {};
  const loads = [loadTemplateFonts(config)];

  for (const url of urls) {
    loads.push(loadImage(url).then(image => {
      images[url] = image;
    }));
  }

  await Promise.all(loads);

  return images;
}

/* ---------- helpers ---------- */

function hexToRgba(hex, alpha) {
  const value = (hex || '#000000').replace('#', '');
  const r = parseInt(value.substring(0, 2), 16);
  const g = parseInt(value.substring(2, 4), 16);
  const b = parseInt(value.substring(4, 6), 16);

  return 'rgba(' + r + ',' + g + ',' + b + ',' + alpha + ')';
}

function roundRectPath(ctx, x, y, w, h, radius) {
  const r = Math.max(0, Math.min(radius || 0, w / 2, h / 2));

  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.arcTo(x + w, y, x + w, y + r, r);
  ctx.lineTo(x + w, y + h - r);
  ctx.arcTo(x + w, y + h, x + w - r, y + h, r);
  ctx.lineTo(x + r, y + h);
  ctx.arcTo(x, y + h, x, y + h - r, r);
  ctx.lineTo(x, y + r);
  ctx.arcTo(x, y, x + r, y, r);
  ctx.closePath();
}

/* shadowBlur e shadowOffset ignoram o transform do canvas, por isso o scale. */
function applyShadow(ctx, scale) {
  ctx.shadowColor = 'rgba(0,0,0,0.35)';
  ctx.shadowBlur = 40 * scale;
  ctx.shadowOffsetX = 0;
  ctx.shadowOffsetY = 12 * scale;
}

function drawCover(ctx, image, x, y, w, h, zoom = 1, focusX = 0.5, focusY = 0.5) {
  const s = Math.max(w / image.naturalWidth, h / image.naturalHeight) * zoom;
  const dw = image.naturalWidth * s;
  const dh = image.naturalHeight * s;
  const dx = x + (w - dw) * focusX;
  const dy = y + (h - dh) * focusY;

  ctx.drawImage(image, dx, dy, dw, dh);
}

function linearGradient(ctx, colors, angle, x, y, w, h) {
  /* Mesmo sentido do CSS: 180 = de cima para baixo. */
  const rad = (angle || 0) * Math.PI / 180;
  const dx = Math.sin(rad);
  const dy = -Math.cos(rad);
  const half = (Math.abs(w * dx) + Math.abs(h * dy)) / 2;
  const cx = x + w / 2;
  const cy = y + h / 2;
  const gradient = ctx.createLinearGradient(cx - dx * half, cy - dy * half, cx + dx * half, cy + dy * half);

  gradient.addColorStop(0, colors[0]);
  gradient.addColorStop(1, colors[1]);

  return gradient;
}

/* ---------- fundo ---------- */

function drawBackground(ctx, background, W, H) {
  ctx.fillStyle = '#cccccc';

  if (background.type === 'color') {
    ctx.fillStyle = background.color;
  }

  if (background.type === 'gradient') {
    ctx.fillStyle = linearGradient(ctx, background.colors, background.angle, 0, 0, W, H);
  }

  ctx.fillRect(0, 0, W, H);
}

/* ---------- elementos ---------- */

function drawPhoto(ctx, element, image, box, scale) {
  const { x, y, w, h } = box;

  if (element.shadow) {
    ctx.save();
    applyShadow(ctx, scale);
    roundRectPath(ctx, x, y, w, h, element.radius);
    ctx.fillStyle = '#000000';
    ctx.fill();
    ctx.restore();
  }

  ctx.save();
  roundRectPath(ctx, x, y, w, h, element.radius);
  ctx.clip();

  if (image) {
    drawCover(ctx, image, x, y, w, h, element.zoom, element.focusX, element.focusY);
  } else {
    ctx.fillStyle = '#cfcfcf';
    ctx.fillRect(x, y, w, h);
  }

  const overlay = element.overlay || {};

  if (overlay.type === 'solid') {
    ctx.fillStyle = hexToRgba(overlay.color, overlay.opacity);
    ctx.fillRect(x, y, w, h);
  }

  if (overlay.type === 'gradient') {
    const solid = hexToRgba(overlay.color, overlay.opacity);
    const clear = hexToRgba(overlay.color, 0);
    const gradient = ctx.createLinearGradient(0, y, 0, y + h);

    if (overlay.direction === 'top') {
      gradient.addColorStop(0, solid);
      gradient.addColorStop(0.6, clear);
    } else if (overlay.direction === 'both') {
      gradient.addColorStop(0, solid);
      gradient.addColorStop(0.3, clear);
      gradient.addColorStop(0.45, clear);
      gradient.addColorStop(1, solid);
    } else {
      gradient.addColorStop(0.3, clear);
      gradient.addColorStop(1, solid);
    }

    ctx.fillStyle = gradient;
    ctx.fillRect(x, y, w, h);
  }

  ctx.restore();

  const border = element.border || {};

  if (border.width > 0) {
    const bw = border.width;

    ctx.save();
    ctx.lineWidth = bw;
    ctx.strokeStyle = border.color;
    roundRectPath(ctx, x + bw / 2, y + bw / 2, w - bw, h - bw, Math.max(0, element.radius - bw / 2));
    ctx.stroke();
    ctx.restore();
  }
}

function drawShape(ctx, element, box, scale) {
  ctx.save();
  ctx.globalAlpha = element.opacity;

  if (element.shadow) {
    applyShadow(ctx, scale);
  }

  roundRectPath(ctx, box.x, box.y, box.w, box.h, element.radius);
  ctx.fillStyle = element.color;
  ctx.fill();
  ctx.restore();
}

function drawIcon(ctx, element, box) {
  const s = element.stroke;
  const x = box.x + s / 2;
  const y = box.y + s / 2;
  const w = box.w - s;
  const h = box.h - s;
  const cx = x + w / 2;
  const cy = y + h / 2;

  ctx.save();
  ctx.strokeStyle = element.color;
  ctx.lineWidth = s;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  ctx.beginPath();

  if (element.icon === 'chevrons-up') {
    ctx.moveTo(x, cy);
    ctx.lineTo(cx, y);
    ctx.lineTo(x + w, cy);
    ctx.moveTo(x, y + h);
    ctx.lineTo(cx, cy);
    ctx.lineTo(x + w, y + h);
  } else if (element.icon === 'arrow-up') {
    ctx.moveTo(cx, y + h);
    ctx.lineTo(cx, y);
    ctx.moveTo(cx - Math.min(w / 2, h * 0.4), y + h * 0.4);
    ctx.lineTo(cx, y);
    ctx.lineTo(cx + Math.min(w / 2, h * 0.4), y + h * 0.4);
  } else if (element.icon === 'arrow-right') {
    ctx.moveTo(x, cy);
    ctx.lineTo(x + w, cy);
    ctx.moveTo(x + w - Math.min(h / 2, w * 0.4), cy - Math.min(h / 2, w * 0.4));
    ctx.lineTo(x + w, cy);
    ctx.lineTo(x + w - Math.min(h / 2, w * 0.4), cy + Math.min(h / 2, w * 0.4));
  } else {
    ctx.moveTo(x, y + h);
    ctx.lineTo(cx, y);
    ctx.lineTo(x + w, y + h);
  }

  ctx.stroke();
  ctx.restore();
}

function setFont(ctx, element, size) {
  const style = element.italic ? 'italic ' : '';
  ctx.font = style + element.weight + ' ' + size + 'px "' + element.font + '", sans-serif';

  if ('letterSpacing' in ctx) {
    ctx.letterSpacing = (element.letterSpacing || 0) + 'px';
  }
}

function breakLongWord(ctx, word, maxWidth) {
  const parts = [];
  let current = '';

  for (const char of word) {
    if (current && ctx.measureText(current + char).width > maxWidth) {
      parts.push(current);
      current = char;
    } else {
      current += char;
    }
  }

  if (current) {
    parts.push(current);
  }

  return parts;
}

function wrapText(ctx, text, maxWidth) {
  const lines = [];
  let current = '';

  for (const word of text.split(/\s+/)) {
    if (!word) {
      continue;
    }

    const candidate = current ? current + ' ' + word : word;

    if (ctx.measureText(candidate).width <= maxWidth) {
      current = candidate;
      continue;
    }

    if (current) {
      lines.push(current);
    }

    if (ctx.measureText(word).width > maxWidth) {
      const parts = breakLongWord(ctx, word, maxWidth);
      current = parts.pop();

      for (const part of parts) {
        lines.push(part);
      }
    } else {
      current = word;
    }
  }

  if (current) {
    lines.push(current);
  }

  return lines;
}

function truncateLines(ctx, lines, limit, maxWidth) {
  if (lines.length <= limit) {
    return lines;
  }

  const kept = lines.slice(0, limit);
  let last = kept[limit - 1] + '…';

  while (last.length > 1 && ctx.measureText(last).width > maxWidth) {
    last = last.slice(0, -2) + '…';
  }

  kept[limit - 1] = last;

  return kept;
}

function isLinesBox(element) {
  return !!(element.box && element.box.enabled && element.box.mode === 'lines');
}

/* Reduz a fonte até o texto caber na caixa, respeitando o tamanho mínimo. */
function layoutText(ctx, element, text, box) {
  const hasBox = element.box && element.box.enabled;
  const padX = hasBox ? element.box.padding : 0;
  /* No modo faixas o fundo acompanha cada linha, então não há margem vertical extra. */
  const padY = hasBox && !isLinesBox(element) ? element.box.padding : 0;
  const maxWidth = Math.max(10, box.w - padX * 2);
  const minSize = Math.min(element.minSize || element.size, element.size);
  let size = element.size;

  for (;;) {
    setFont(ctx, element, size);

    const lineHeight = size * element.lineHeight;
    const lines = wrapText(ctx, text, maxWidth);
    const fits = lines.length <= element.maxLines && lines.length * lineHeight + padY * 2 <= box.h + 1;

    if (fits || size <= minSize) {
      const byHeight = Math.max(1, Math.floor((box.h - padY * 2 + 1) / lineHeight));
      const limit = Math.min(element.maxLines, byHeight);

      return { size, lineHeight, padX, padY, lines: truncateLines(ctx, lines, limit, maxWidth) };
    }

    size = Math.max(minSize, size - 2);
  }
}

function drawText(ctx, element, text, box, scale) {
  if (element.uppercase) {
    text = text.toLocaleUpperCase('pt-BR');
  }

  const { lines, lineHeight, padX, padY, size } = layoutText(ctx, element, text, box);

  if (!lines.length) {
    return;
  }

  const widths = lines.map(line => ctx.measureText(line).width);
  const maxLineWidth = Math.max(...widths);
  const blockHeight = lines.length * lineHeight + padY * 2;
  const hasBox = element.box && element.box.enabled;
  const linesBox = isLinesBox(element);
  let blockWidth = box.w;

  if (hasBox) {
    blockWidth = Math.min(box.w, maxLineWidth + padX * 2);
  }

  let top = box.y;

  if (element.valign === 'middle') {
    top = box.y + (box.h - blockHeight) / 2;
  } else if (element.valign === 'bottom') {
    top = box.y + box.h - blockHeight;
  }

  let blockX = box.x;
  let textX = box.x + padX;

  if (element.align === 'center') {
    blockX = box.x + (box.w - blockWidth) / 2;
    textX = box.x + box.w / 2;
  } else if (element.align === 'right') {
    blockX = box.x + box.w - blockWidth;
    textX = box.x + box.w - padX;
  }

  if (hasBox) {
    ctx.save();
    ctx.globalAlpha = element.box.opacity;
    ctx.fillStyle = element.box.color;

    if (linesBox) {
      /* Uma faixa por linha, como manchete destacada com marca-texto. */
      const stripHeight = Math.min(lineHeight, size * 1.22);

      for (let i = 0; i < lines.length; i++) {
        const stripWidth = widths[i] + padX * 2;
        let stripX = textX - padX;

        if (element.align === 'center') {
          stripX = textX - stripWidth / 2;
        } else if (element.align === 'right') {
          stripX = textX + padX - stripWidth;
        }

        const centerY = top + i * lineHeight + lineHeight / 2;
        roundRectPath(ctx, stripX, centerY - stripHeight / 2, stripWidth, stripHeight, element.box.radius);
        ctx.fill();
      }
    } else {
      roundRectPath(ctx, blockX, top, blockWidth, blockHeight, element.box.radius);
      ctx.fill();
    }

    ctx.restore();
  }

  ctx.save();

  if (element.shadow) {
    ctx.shadowColor = 'rgba(0,0,0,0.45)';
    ctx.shadowBlur = 24 * scale;
    ctx.shadowOffsetY = 3 * scale;
  }

  ctx.fillStyle = element.color;
  ctx.textAlign = element.align;
  ctx.textBaseline = 'middle';

  for (let i = 0; i < lines.length; i++) {
    ctx.fillText(lines[i], textX, top + padY + i * lineHeight + lineHeight / 2);
  }

  ctx.restore();
}

/* ---------- API ---------- */

export function drawTemplate(ctx, formatKey, config, data, images, scale = 1) {
  const format = FORMATS[formatKey];
  const W = format.width;
  const H = format.height;

  ctx.save();
  ctx.setTransform(scale, 0, 0, scale, 0, 0);
  ctx.clearRect(0, 0, W, H);
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';

  drawBackground(ctx, config.background || {}, W, H);

  for (const element of config.elements || []) {
    if (element.hidden) {
      continue;
    }

    const box = { x: element.x * W, y: element.y * H, w: element.w * W, h: element.h * H };
    const url = elementImageUrl(element, data);
    const image = url ? images[url] : null;

    ctx.save();

    if (element.type === 'photo') {
      drawPhoto(ctx, element, image, box, scale);
    } else if (element.type === 'shape') {
      drawShape(ctx, element, box, scale);
    } else if (element.type === 'icon') {
      drawIcon(ctx, element, box);
    } else if (element.type === 'text') {
      const text = resolveText(element.content, data);

      if (text) {
        drawText(ctx, element, text, box, scale);
      }
    }

    ctx.restore();
  }

  ctx.restore();
}

/* Ajusta o tamanho do canvas, carrega o necessário e desenha. */
export async function renderTemplate(canvas, formatKey, config, data, scale = 1) {
  const images = await prepareAssets(config, data);
  const format = FORMATS[formatKey];
  const width = Math.round(format.width * scale);
  const height = Math.round(format.height * scale);

  if (canvas.width !== width) {
    canvas.width = width;
  }

  if (canvas.height !== height) {
    canvas.height = height;
  }

  drawTemplate(canvas.getContext('2d'), formatKey, config, data, images, scale);

  return images;
}

export async function exportTemplate(formatKey, config, data) {
  const canvas = document.createElement('canvas');

  await renderTemplate(canvas, formatKey, config, data, 1);

  return new Promise((resolve, reject) => {
    canvas.toBlob(blob => {
      if (blob) {
        resolve(blob);
      } else {
        reject(new Error('Falha ao gerar a imagem'));
      }
    }, 'image/jpeg', 0.92);
  });
}
